import { useCallback, useEffect, useState } from 'react';

/**
 * useNavState (C4 · W2/W5) — the progressive-nav milestone ladder.
 *
 * Reveals sidebar sections as the household hits milestones, with a `showAll`
 * escape hatch and reversible per-user curation (`hidden`/`pinned`). The
 * "Advanced" group (Chart of Accounts · Balance Sheet · Reports) never fronts —
 * it stays out of the phased reveal so the no-jargon promise holds (W5).
 *
 * Used only when `flags.progressiveNav` is on (flag-off = today's full nav).
 *
 * Today `progress` is local + persisted; at go-live it becomes read-only off
 * `OnboardingProgress` + Household link state (the milestones below map 1:1 to
 * onboarding completion). Curation persistence is per-user.
 */

export const NAV_PHASES = ['Start', 'Money basics', 'Tax', 'Plan'] as const;

export interface NavMilestone {
  phase: number;
  label: string;
  unlocks: string;
}

export const NAV_MILESTONES: NavMilestone[] = [
  { phase: 1, label: 'Categorize your first transactions', unlocks: 'Revenue + Expenses' },
  { phase: 2, label: 'Finish tax setup', unlocks: 'Tax + Deductions' },
  { phase: 3, label: 'Set your first goal', unlocks: 'Budgets, Goals, Retirement, Forecasting' },
];

const STORAGE_KEY = 'fhq-nav-state';

interface PersistedNavState {
  progress: number;
  hidden: string[];
  pinned: string[];
}

function readPersisted(): PersistedNavState {
  if (typeof window === 'undefined') return { progress: 0, hidden: [], pinned: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { progress: 0, hidden: [], pinned: [] };
    const parsed = JSON.parse(raw) as Partial<PersistedNavState>;
    return {
      progress: Math.min(3, Math.max(0, parsed.progress ?? 0)),
      hidden: parsed.hidden ?? [],
      pinned: parsed.pinned ?? [],
    };
  } catch {
    return { progress: 0, hidden: [], pinned: [] };
  }
}

export interface NavState {
  /** Milestone progress, 0–3. */
  progress: number;
  setProgress: (next: number) => void;
  advance: () => void;
  /** Reveal everything (escape hatch). */
  skip: () => void;
  /** Collapse back to the phased view. */
  reset: () => void;
  /** Clear progress + curation. */
  resetAll: () => void;
  showAll: boolean;
  setShowAll: (next: boolean) => void;
  hidden: Set<string>;
  toggleHidden: (id: string) => void;
  pinned: Set<string>;
  togglePinned: (id: string) => void;
  /** The highest phase currently visible (3 when `showAll`). */
  visiblePhase: number;
}

export function useNavState(): NavState {
  const [{ progress, hidden, pinned }, setState] = useState<{
    progress: number;
    hidden: Set<string>;
    pinned: Set<string>;
  }>(() => {
    const p = readPersisted();
    return { progress: p.progress, hidden: new Set(p.hidden), pinned: new Set(p.pinned) };
  });
  const [showAll, setShowAll] = useState(false);

  // Persist progress + curation (showAll is session-only, not persisted).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          progress,
          hidden: [...hidden],
          pinned: [...pinned],
        } satisfies PersistedNavState),
      );
    } catch {
      // private mode — ignore
    }
  }, [progress, hidden, pinned]);

  const setProgress = useCallback(
    (next: number) =>
      setState((s) => ({ ...s, progress: Math.min(3, Math.max(0, next)) })),
    [],
  );
  const advance = useCallback(
    () => setState((s) => ({ ...s, progress: Math.min(3, s.progress + 1) })),
    [],
  );
  const skip = useCallback(() => setShowAll(true), []);
  const reset = useCallback(() => setShowAll(false), []);
  const resetAll = useCallback(() => {
    setShowAll(false);
    setState({ progress: 0, hidden: new Set(), pinned: new Set() });
  }, []);
  const toggleHidden = useCallback(
    (id: string) =>
      setState((s) => {
        const n = new Set(s.hidden);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return { ...s, hidden: n };
      }),
    [],
  );
  const togglePinned = useCallback(
    (id: string) =>
      setState((s) => {
        const n = new Set(s.pinned);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return { ...s, pinned: n };
      }),
    [],
  );

  return {
    progress,
    setProgress,
    advance,
    skip,
    reset,
    resetAll,
    showAll,
    setShowAll,
    hidden,
    toggleHidden,
    pinned,
    togglePinned,
    visiblePhase: showAll ? 3 : progress,
  };
}
